import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Alert,
  Button,
  InputNumber,
  Layout,
  Radio,
  Space,
  Spin,
  Switch,
  Tooltip
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import {
  fetchScoringConfig,
  saveScoringConfig,
  resetScoringConfig
} from '../../../../redux/actions/trm/scoringConfig';
import { fetchDefaultScoringConfig } from '../../../../redux/actions/trm/defaultScoringConfig';
import { fetchAMLSAlertLevel } from '../../../../redux/actions/common';
import AlertLevelBandSlider from './AlertLevelBandSlider';
import { DEFAULT_DEVIATION_MAX_CONFIG, scoringConfigEquals } from './defaults';
import {
  AGGREGATION_LABELS,
  FIELD_LABELS,
  TIPS,
  bandErrors,
  deviationErrors
} from './scoringCopy';
import style from './index.scss';

const { Content } = Layout;

const isDisabledRow = (data) => data && data.engineType === 'Disabled';

const InfoTip = ({ title }) => (
  <Tooltip title={title} placement="topLeft">
    <InfoCircleOutlined className={style['info-icon']} />
  </Tooltip>
);
InfoTip.propTypes = { title: PropTypes.node.isRequired };

const FieldLabel = ({ children, tip, htmlFor }) => (
  <label className={style['field-label']} htmlFor={htmlFor}>
    {children}
    {tip && <InfoTip title={tip} />}
  </label>
);
FieldLabel.propTypes = {
  children: PropTypes.node.isRequired,
  tip: PropTypes.node,
  htmlFor: PropTypes.string
};
FieldLabel.defaultProps = { tip: null, htmlFor: undefined };

const PanelTitle = ({ children, tip }) => (
  <div className={style['panel-title']}>
    {children}
    {tip && <InfoTip title={tip} />}
  </div>
);
PanelTitle.propTypes = {
  children: PropTypes.node.isRequired,
  tip: PropTypes.node
};
PanelTitle.defaultProps = { tip: null };

const AlertScoringTab = ({
  scenarioId,
  isScenarioLocked,
  data,
  loading,
  saving,
  alertLevels,
  globalDefault,
  fetchScoringConfigAction,
  saveScoringConfigAction,
  resetScoringConfigAction,
  fetchDefaultScoringConfigAction,
  fetchAMLSAlertLevelAction
}) => {
  const [form, setForm] = useState(DEFAULT_DEVIATION_MAX_CONFIG);

  useEffect(() => {
    if (scenarioId) fetchScoringConfigAction({ scenarioId });
  }, [scenarioId, fetchScoringConfigAction]);

  // FIN-12215: load the tenant-level default scoring config so the "Defaults
  // shown" baseline and the Reset-to-defaults target reflect the tenant global
  // default (Settings > Scoring) instead of the shipped constant.
  useEffect(() => {
    fetchDefaultScoringConfigAction();
  }, [fetchDefaultScoringConfigAction]);

  // FIN-12134: load the custom alert-level names/colours so the bands show the
  // configured Level Name Setting instead of hardcoded Low/Medium/High.
  useEffect(() => {
    if (!alertLevels?.length) fetchAMLSAlertLevelAction();
  }, []);

  // The tenant default is the fallback baseline when a scenario has no override.
  const tenantDefaults = useMemo(
    () =>
      globalDefault && globalDefault.engineType === 'DeviationMax'
        ? { ...DEFAULT_DEVIATION_MAX_CONFIG, ...globalDefault }
        : DEFAULT_DEVIATION_MAX_CONFIG,
    [globalDefault]
  );

  // Server-side baseline the form is compared against for dirty-detection:
  // the scenario override when present, otherwise the tenant default.
  const baseline = useMemo(
    () =>
      data && data.engineType === 'DeviationMax'
        ? { ...DEFAULT_DEVIATION_MAX_CONFIG, ...data }
        : tenantDefaults,
    [data, tenantDefaults]
  );

  // Re-sync the form whenever the server-side baseline changes (initial load,
  // after save, or when the tenant default arrives). User edits happen between
  // these server updates, so this never clobbers in-progress edits.
  useEffect(() => {
    if (!isDisabledRow(data)) setForm(baseline);
  }, [baseline]);

  const showingDefaults = !data;
  const showingLegacyDisabled = isDisabledRow(data);
  const readOnly = isScenarioLocked || showingLegacyDisabled;
  const isDirty = useMemo(
    () => !scoringConfigEquals(form, baseline),
    [form, baseline]
  );

  // FIN-13794 (adversarial review P2-V1) — band rules (incl. `lowMin`) gate Save here too so an
  // invalid triple never reaches the BE. FIN-14015 — band messages are shown once, inline by
  // the slider (same input: lowMin ?? 0); this list holds only the other field errors.
  const validation = useMemo(() => deviationErrors(form), [form]);
  const bandsInvalid = useMemo(
    () =>
      bandErrors({ ...form.thresholds, lowMin: form.thresholds.lowMin ?? 0 })
        .length > 0,
    [form.thresholds]
  );

  const onSave = () => saveScoringConfigAction({ scenarioId, config: form });
  // FIN-12215: for a normal DeviationMax scenario, Reset reverts the form to the
  // tenant/global defaults in-UI (the user then clicks Save to persist). For a
  // legacy "Disabled" row it keeps the server-side clear so the row can be
  // migrated off the disabled engine.
  const onReset = () => {
    if (showingLegacyDisabled) {
      resetScoringConfigAction({ scenarioId });
    } else {
      setForm(tenantDefaults);
    }
  };

  // FIN-13794 (Q10 revised) — the slider now emits `{lowMin, lowMax, mediumMax}` after MIN→MAX
  // translation. Spread `...p.thresholds` first so any UNTOUCHED field (defaultActionScores etc.)
  // survives, then overlay the three fields the slider owns.
  const updateThresholds = ({ lowMin, lowMax, mediumMax }) =>
    setForm((p) => ({
      ...p,
      thresholds: { ...p.thresholds, lowMin, lowMax, mediumMax }
    }));

  if (loading && !data) return <Spin />;

  return (
    <Content>
      <Space direction="vertical" className={style.tabPanelSpace}>
        <Content className={style.site_layout_background}>
          <div className={style['alert-scoring-tab']}>
            <div className={style.header}>
              <div className={style['header-title']}>Alert Scoring</div>
              <div className={style['header-subtitle']}>
                Per-scenario score thresholds and deviation behavior.
              </div>
            </div>

            {showingLegacyDisabled && (
              <div className={style['legacy-disabled-banner']}>
                Scoring disabled (legacy). Click Reset to switch to default
                scoring.
              </div>
            )}
            {showingDefaults && !showingLegacyDisabled && (
              <Alert
                className={style['defaults-banner']}
                type="info"
                showIcon
                message="These are the tenant default settings. Click Save to use custom settings for this scenario."
              />
            )}

            <div className={style.panel}>
              <PanelTitle tip={TIPS.bands}>Alert Level Bands</PanelTitle>
              {/* FIN-13794 (Q10 revised) — three MIN-oriented inputs; lowMin lives in
                  the same component so scenario overrides can't silently drop it. */}
              <AlertLevelBandSlider
                lowMin={form.thresholds.lowMin ?? 0}
                lowMax={form.thresholds.lowMax}
                mediumMax={form.thresholds.mediumMax}
                onChange={updateThresholds}
                disabled={readOnly}
                alertLevels={alertLevels}
              />
            </div>

            {!showingLegacyDisabled && (
              <>
                <div className={style.panel}>
                  <PanelTitle tip={TIPS.aggregation}>
                    Aggregation Mode
                  </PanelTitle>
                  <Radio.Group
                    value={form.aggregationMode}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        aggregationMode: e.target.value
                      }))
                    }
                    disabled={readOnly}
                  >
                    <Radio value="Max">{AGGREGATION_LABELS.Max}</Radio>
                    <Radio value="SumCapped">
                      {AGGREGATION_LABELS.SumCapped}
                    </Radio>
                  </Radio.Group>
                </div>

                <div className={style.panel}>
                  <PanelTitle tip={TIPS.deviation}>
                    Deviation Modifier
                  </PanelTitle>
                  <div className={style['switch-row']}>
                    <Switch
                      id="deviationEnabled"
                      checked={form.deviationEnabled}
                      onChange={(v) =>
                        setForm((p) => ({ ...p, deviationEnabled: v }))
                      }
                      disabled={readOnly}
                    />
                    <label
                      htmlFor="deviationEnabled"
                      className={style['switch-label']}
                    >
                      Enable deviation modifier
                    </label>
                  </div>
                  {form.deviationEnabled && (
                    <div className={style['deviation-fields']}>
                      <div className={style.field}>
                        <FieldLabel
                          htmlFor="minFraction"
                          tip={TIPS.minFraction}
                        >
                          {FIELD_LABELS.minFraction}
                        </FieldLabel>
                        <InputNumber
                          id="minFraction"
                          aria-label={FIELD_LABELS.minFraction}
                          min={0}
                          max={1}
                          step={0.05}
                          value={form.minFraction}
                          onChange={(v) =>
                            setForm((p) => ({ ...p, minFraction: v }))
                          }
                          disabled={readOnly}
                        />
                      </div>
                      <div className={style.field}>
                        <FieldLabel htmlFor="capRatio" tip={TIPS.capRatio}>
                          {FIELD_LABELS.capRatio}
                        </FieldLabel>
                        <InputNumber
                          id="capRatio"
                          aria-label={FIELD_LABELS.capRatio}
                          min={1.01}
                          step={0.5}
                          value={form.capRatio}
                          onChange={(v) =>
                            setForm((p) => ({ ...p, capRatio: v }))
                          }
                          disabled={readOnly}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {validation.length > 0 && (
              <Alert
                type="error"
                showIcon
                message={validation.join('; ')}
                style={{ marginBottom: 16 }}
              />
            )}

            <div className={style.footer}>
              <div className={style['footer-meta']}>
                Saved scenario-scoped values take effect at the next champion /
                challenger promotion.
              </div>
              <div className={style['footer-actions']}>
                <Button
                  onClick={onReset}
                  disabled={
                    saving ||
                    isScenarioLocked ||
                    (showingLegacyDisabled ? false : !isDirty)
                  }
                >
                  Reset to defaults
                </Button>
                {!showingLegacyDisabled && (
                  <Button
                    type="primary"
                    onClick={onSave}
                    loading={saving}
                    disabled={readOnly || bandsInvalid || validation.length > 0}
                  >
                    Save
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Content>
      </Space>
    </Content>
  );
};

AlertScoringTab.propTypes = {
  scenarioId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  isScenarioLocked: PropTypes.bool,
  data: PropTypes.object,
  loading: PropTypes.bool,
  saving: PropTypes.bool,
  alertLevels: PropTypes.array,
  globalDefault: PropTypes.object,
  fetchScoringConfigAction: PropTypes.func.isRequired,
  saveScoringConfigAction: PropTypes.func.isRequired,
  resetScoringConfigAction: PropTypes.func.isRequired,
  fetchDefaultScoringConfigAction: PropTypes.func.isRequired,
  fetchAMLSAlertLevelAction: PropTypes.func.isRequired
};
AlertScoringTab.defaultProps = {
  isScenarioLocked: false,
  data: null,
  loading: false,
  saving: false,
  alertLevels: [],
  globalDefault: null
};

const mapStateToProps = (state) => ({
  data: state.scoringConfig.data,
  loading: state.scoringConfig.loading,
  saving: state.scoringConfig.saving,
  alertLevels: state.amlsCommon.alertLevelConfigs?.data,
  globalDefault: state.defaultScoringConfig?.data
});

const mapDispatchToProps = {
  fetchScoringConfigAction: fetchScoringConfig,
  saveScoringConfigAction: saveScoringConfig,
  resetScoringConfigAction: resetScoringConfig,
  fetchDefaultScoringConfigAction: fetchDefaultScoringConfig,
  fetchAMLSAlertLevelAction: fetchAMLSAlertLevel
};

export default connect(mapStateToProps, mapDispatchToProps)(AlertScoringTab);
